import { NgFor, NgIf } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IconComponent, TypographyComponent } from 'components';
import {
	GetSocialListeningMyBrandAnalyticsTopSitesByVisitsService,
	TopSiteByVisits
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-top-sites-by-visits.service';
import {
	PreviewSlide,
	ReportDateRange,
	SummaryTextWidget,
	TitleWidget
} from '../../../../models/preview/preview.models';
import { ReportContentItem } from '../../../../services/report-settings.service';
import {
	applyContentItemOverrides,
	formatCompactNumber
} from '../../../../services/report-preview-utils';
import { SlideNoDataComponent } from '../slide-no-data/slide-no-data.component';
import { ReportInlineTextComponent } from '../../components/report-inline-text/report-inline-text.component';
import {
	NO_SLIDE_TEXT_FIELDS,
	ReportSlideTextFields,
	slideTextFields
} from '../../../../services/report-inline-edit.service';
import { ReportBlockRender } from '../../../../services/report-blocks.service';
import {
	ReportSectionChartSwitch,
	sectionChartSource,
	sectionChartSwitch
} from '../../../../services/report-chart-types';
import { ReportChartSwitchComponent } from '../../components/report-chart-switch/report-chart-switch.component';
import { ReportSlideBrandComponent } from '../../components/report-slide-brand/report-slide-brand.component';

const MAX_SITES = 10;

// Categorical bar palette — one color per site row, in order, mirroring the
// LOLLY report mockup (Figma 13302:101017). Cycles if there are >10 rows.
const BAR_COLORS = [
	'#2872e1',
	'#e94f6a',
	'#2bb673',
	'#a98cf0',
	'#f5c451',
	'#6cd6c8',
	'#f0a3c6',
	'#5b8def',
	'#9aa7c7',
	'#cbb892'
];

interface SiteBar {
	id: string;
	name: string;
	valueLabel: string;
	color: string;
	widthPct: number;
}

@Component({
	standalone: true,
	selector: 'app-top-sites-by-visits-slide',
	templateUrl: './top-sites-by-visits-slide.component.html',
	styleUrls: ['./top-sites-by-visits-slide.component.scss'],
	imports: [
		NgFor,
		NgIf,
		IconComponent,
		TypographyComponent,
		SlideNoDataComponent,
		ReportInlineTextComponent,
		ReportChartSwitchComponent,
		ReportSlideBrandComponent
	]
})
export class TopSitesByVisitsSlideComponent implements OnChanges, OnDestroy {
	@Input() item!: ReportContentItem;
	@Input() brandId!: string;
	@Input() dateRange!: ReportDateRange;
	@Input() themeColor: string | null = null;
	@Input() editorMode = false;
	@Output() slideBuilt = new EventEmitter<PreviewSlide>();
	/** The reader picked another type for this section's chart. */
	@Output() chartTypeChange = new EventEmitter<ReportBlockRender | null>();

	/** The heading and summary the reader can type into, in the editor. */
	textFields: ReportSlideTextFields = NO_SLIDE_TEXT_FIELDS;

	title = '';
	dateLabel = '';
	summaryText = '';
	titleColor = '#2872e1';
	bars: SiteBar[] = [];
	totalVisitsLabel = '';
	/** What the chart is drawn as: its own design until the reader switches. */
	chartSwitch: ReportSectionChartSwitch = sectionChartSwitch(
		'top_sites_by_visits',
		null,
		null
	);

	get isEmpty(): boolean {
		return !this.bars.length;
	}

	private readonly destroy$ = new Subject<void>();
	private cachedSites: TopSiteByVisits[] | null = null;
	private cacheKey: string | null = null;

	constructor(
		private readonly analyticsService: GetSocialListeningMyBrandAnalyticsTopSitesByVisitsService
	) {}

	ngOnChanges(): void {
		this.destroy$.next();
		if (!this.item || !this.brandId || !this.dateRange) return;

		const currentCacheKey = `${this.brandId}:${this.dateRange.start}:${this.dateRange.end}`;
		if (this.cachedSites && this.cacheKey === currentCacheKey) {
			this.applySites(this.cachedSites);
			return;
		}

		this.analyticsService
			.fetch(
				this.brandId,
				new Date(this.dateRange.start),
				undefined,
				new Date(this.dateRange.end)
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe(data => {
				this.cachedSites = data;
				this.cacheKey = currentCacheKey;
				this.applySites(data);
			});
	}

	private applySites(data: TopSiteByVisits[]): void {
		const sorted = [...data]
			.sort((a, b) => b.visits - a.visits)
			.slice(0, MAX_SITES);
		const rawSlide = this.buildSlide(sorted);
		const slide = applyContentItemOverrides(
			rawSlide,
			this.item,
			this.themeColor
		);
		this.updateViewModel(slide, sorted);
		this.slideBuilt.emit(slide);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private buildSlide(sites: TopSiteByVisits[]): PreviewSlide {
		const prefix = `${this.item.id}_${this.brandId}`;
		return {
			id: `${prefix}_slide`,
			slideType: 'top_sites_by_visits',
			title: this.item.title || 'Top Sites by Visits',
			description: this.item.description || '',
			dateRange: this.dateRange,
			widgets: [
				{
					id: `${prefix}_title`,
					kind: 'title',
					visible: true,
					content: {
						text: this.item.title || 'Top Sites by Visits',
						color: '#2872e1'
					}
				},
				{
					id: `${prefix}_date`,
					kind: 'date_range',
					visible: true,
					content: { text: this.dateRange.label }
				},
				{
					id: `${prefix}_summary`,
					kind: 'summary_text',
					visible: true,
					content: { text: this.item.description || '' }
				}
			]
		};
	}

	private updateViewModel(
		slide: PreviewSlide,
		sites: TopSiteByVisits[]
	): void {
		this.textFields = slideTextFields(slide);
		const hidden = new Set(slide.overrides?.hiddenWidgetIds ?? []);

		const titleWidget = slide.widgets.find(
			widget => widget.kind === 'title' && !hidden.has(widget.id)
		) as TitleWidget | undefined;
		const dateWidget = slide.widgets.find(
			widget => widget.kind === 'date_range' && !hidden.has(widget.id)
		) as TitleWidget | undefined;
		const summaryWidget = slide.widgets.find(
			widget => widget.kind === 'summary_text' && !hidden.has(widget.id)
		) as SummaryTextWidget | undefined;

		this.title = titleWidget?.content.text ?? slide.title;
		this.titleColor = titleWidget?.content.color ?? '#2872e1';
		this.dateLabel =
			dateWidget?.content.text ?? slide.dateRange?.label ?? '';
		this.summaryText = summaryWidget?.content.text ?? '';

		this.buildBars(sites);
	}

	private buildBars(sites: TopSiteByVisits[]): void {
		const maxVal = sites.reduce((m, s) => Math.max(m, s.visits), 0);
		let total = 0;

		this.bars = sites.map((site, i) => {
			const hasVisits = site.visitsStatus === 'ok' && site.visits > 0;
			if (hasVisits) total += site.visits;
			return {
				id: site.name,
				name: site.name,
				valueLabel: hasVisits
					? site.visits.toLocaleString('en-US')
					: '—',
				color: BAR_COLORS[i % BAR_COLORS.length],
				// Cap the longest bar at 85% so the value label that follows it
				// (site visits can be 8 digits) doesn't run off the right edge.
				widthPct: maxVal > 0 ? (site.visits / maxVal) * 85 : 0
			};
		});

		// KPI callout — total referral visits across the listed sites.
		this.totalVisitsLabel = total > 0 ? formatCompactNumber(total) : '';

		// The same sites, in the colours their bars have here. A site whose
		// visits could not be read is a gap in any other drawing, as it is a
		// dash here — never a zero nobody measured.
		this.chartSwitch = sectionChartSwitch(
			this.item.id,
			this.item.chartType,
			sectionChartSource(this.item.id, {
				labels: sites.map(site => site.name),
				series: [
					{
						key: 'visits',
						label: 'Visits',
						values: sites.map(site =>
							site.visitsStatus === 'ok' && site.visits > 0
								? site.visits
								: null
						)
					}
				],
				labelColors: this.bars.map(bar => bar.color),
				labelColumn: 'Site'
			})
		);
	}

	trackByBarId(_index: number, bar: SiteBar): string {
		return bar.id;
	}
}
